import type {
  GetSurveyResultResponse,
  PostImageSurveyInteractiveQuestionRequestBody,
  PostSurveyInteractiveQuestionRequestBody,
  PostWidgetInteractiveQuestionRequestBody,
} from '@genially/contracts';
import { InteractiveQuestionType } from '@genially/document-model';
import type {
  InteractiveQuestionState,
  InteractiveQuestionsState,
  PlayerUserAnswer,
} from '@genially/p2p-lib';
import { isPlayerUserAnswerScoreable } from '@genially/p2p-lib';
import { SidebarType } from '@genially/ui/build/components/SidebarSummary/types/SidebarType';
import { calculateSidebarTypeFromSelectedQuestion } from '@genially/ui/build/components/SidebarSummary/utils/calculateSidebarTypeFromSelectedQuestion';
import { calculateVotesAverage } from '@genially/ui/build/components/SidebarSummary/utils/calculateVotesAverage';
import { isInteractiveQuestionItemData } from '@genially/ui/build/components/SidebarSummary/utils/isInteractiveQuestion';

import type { PlayPostMessageInteractiveQuestion } from '../../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';
import {
  buildParticipantUsernames,
  latestAnswerPerPlayer,
  toPercentage,
} from './sessionAnswers';

/**
 * The graphic each resolved question gets in the analytics panel. One variant per way of
 * reading a question, not per question type: surveys and text-categorizable widgets both
 * land on `topOption`, and every autocorrective type lands on `hitRate`.
 */
export type SessionAnalyticsChart =
  | { kind: 'hitRate'; percentage: number }
  | {
      kind: 'topOption';
      /** Undefined for image survey options with no caption; the panel numbers those. */
      label: string | undefined;
      optionPosition: number;
      percentage: number;
    }
  | { kind: 'average'; average: number; scale: number }
  | { kind: 'openAnswer' };

export type SessionAnalyticsQuestion = {
  question: PlayPostMessageInteractiveQuestion;
  /** Random questions are listed under a generic label: the drawn statement lives in the
   * question bank, which would cost an extra fetch this panel deliberately avoids. */
  isRandom: boolean;
  /** Votes for `topOption`, distinct answering players for every other chart. */
  responses: number;
  chart: SessionAnalyticsChart;
};

export type SessionAnalyticsKpis = {
  resolvedQuestions: number;
  totalParticipants: number;
  /** 0..100 */
  averageParticipation: number;
  /** 0..100 */
  hitRate: number;
  /**
   * 0..100. Counted on its own rather than derived as `100 - hitRate`, so the two can
   * only ever drift by a rounding point instead of papering over a miscount.
   */
  errorRate: number;
  totalAnswers: number;
};

export type SessionAnalyticsParticipant = {
  username: string;
  /** Read over `scoreableQuestions`: the same denominator for everyone. */
  correctAnswers: number;
};

export type SessionAnalytics = {
  /**
   * Undefined until the host resolves the first question — the panel shows a dash on
   * every card rather than zeroes, so "the session hasn't started" never reads as "it's
   * going badly". Once there is anything resolved, real values show, zero included.
   */
  kpis: SessionAnalyticsKpis | undefined;
  questions: SessionAnalyticsQuestion[];
  /** Only the full-screen view reads these two; the side panel ignores them. */
  participants: SessionAnalyticsParticipant[];
  /** Resolved autocorrective questions: what every participant's score is read over. */
  scoreableQuestions: number;
};

const isBranchingScenario = (question: PlayPostMessageInteractiveQuestion) =>
  isInteractiveQuestionItemData(question) &&
  question.Type === InteractiveQuestionType.BranchingScenario;

const isRandomQuestion = (question: PlayPostMessageInteractiveQuestion) =>
  isInteractiveQuestionItemData(question) &&
  question.Type === InteractiveQuestionType.Random;

/** Latest timestamp across a question's answers; undefined when nobody answered it. */
const lastAnsweredAt = (answers: PlayerUserAnswer[]) =>
  answers.length === 0
    ? undefined
    : Math.max(...answers.map(({ answer }) => answer.timestamp));

/**
 * Read over the participants of the session, not over the answers received: two correct
 * answers out of two in a room of twenty is a 10% hit rate, and saying 100% would tell
 * the host the class understood when all that is known is that two people did.
 */
const buildHitRateChart = (answers: PlayerUserAnswer[], totalParticipants: number) => {
  const scoreable = answers.filter(isPlayerUserAnswerScoreable);
  const correct = scoreable.filter(({ isCorrect }) => isCorrect).length;

  return {
    chart: { kind: 'hitRate', percentage: toPercentage(correct, totalParticipants) },
    responses: scoreable.length,
  } as const;
};

/**
 * Vote tally for surveys and image surveys. The document's own `Answers` order drives the
 * scan, so a tie resolves to whichever option the author placed first rather than to
 * whatever order the answers happened to arrive in.
 */
const buildSurveyTally = (
  question: PlayPostMessageInteractiveQuestion,
  answers: PlayerUserAnswer[],
) => {
  const votesByAnswerId = new Map<string, number>();

  answers.forEach(({ answer }) => {
    const { answerIds } = answer as
      | PostSurveyInteractiveQuestionRequestBody
      | PostImageSurveyInteractiveQuestionRequestBody;

    (answerIds ?? []).forEach(answerId => {
      votesByAnswerId.set(answerId, (votesByAnswerId.get(answerId) ?? 0) + 1);
    });
  });

  const totalVotes = Array.from(votesByAnswerId.values()).reduce(
    (total, votes) => total + votes,
    0,
  );

  const options: { Id: string; Text?: string }[] =
    'Answers' in question
      ? ((question.Answers as { Id: string; Text?: string }[]) ?? [])
      : [];

  let topIndex = -1;
  let topVotes = -1;

  options.forEach((option, index) => {
    const votes = votesByAnswerId.get(option.Id) ?? 0;

    if (votes > topVotes) {
      topVotes = votes;
      topIndex = index;
    }
  });

  const topOption = topIndex === -1 ? undefined : options[topIndex];

  return {
    chart: {
      kind: 'topOption',
      label: topOption?.Text || undefined,
      optionPosition: topIndex + 1,
      percentage: toPercentage(Math.max(topVotes, 0), totalVotes),
    },
    responses: totalVotes,
  } as const;
};

/**
 * Categorizable widgets group by the numeric `score` when they carry one and by the free
 * text otherwise — mirroring how the individual summary aggregates them, so the two views
 * can't group the same answers differently.
 */
const buildCategorizableResult = (
  interactiveQuestionId: string,
  answers: PlayerUserAnswer[],
): GetSurveyResultResponse => {
  const groups = new Map<
    string,
    { totalAnswers: number; score?: { value: number; max: number } }
  >();

  answers.forEach(({ answer }) => {
    const { userAnswer } = answer as PostWidgetInteractiveQuestionRequestBody;
    const { score, answerText } = userAnswer ?? {};

    const answerId = score !== undefined ? String(score.value) : answerText;

    if (answerId === undefined) {
      return;
    }

    const group = groups.get(answerId) ?? { totalAnswers: 0 };

    // A rating scale edited mid-session can send the same value with different maxes;
    // keep the largest so the reported scale stays consistent.
    if (
      score !== undefined &&
      (group.score === undefined || score.max > group.score.max)
    ) {
      group.score = score;
    }

    group.totalAnswers += 1;
    groups.set(answerId, group);
  });

  return Array.from(groups.entries()).map(([answerId, { totalAnswers, score }]) => ({
    geniallyId: '',
    interactiveQuestionId,
    answerId,
    totalAnswers,
    ...(score !== undefined ? { score } : {}),
  }));
};

const buildCategorizableChart = (
  interactiveQuestionId: string,
  answers: PlayerUserAnswer[],
) => {
  const result = buildCategorizableResult(interactiveQuestionId, answers);
  const { average, isNumeric, scale, totalVotes } = calculateVotesAverage(result);

  if (isNumeric && scale !== undefined) {
    return {
      chart: { kind: 'average', average, scale },
      responses: answers.length,
    } as const;
  }

  // Text-categorizable widgets have no scale to average, so they read like a survey.
  const sorted = [...result].sort((a, b) => b.totalAnswers - a.totalAnswers);
  const top = sorted[0];

  return {
    chart: {
      kind: 'topOption',
      label: top?.answerId,
      optionPosition: 1,
      percentage: toPercentage(top?.totalAnswers ?? 0, totalVotes),
    },
    responses: totalVotes,
  } as const;
};

const buildChart = (
  question: PlayPostMessageInteractiveQuestion,
  answers: PlayerUserAnswer[],
  totalParticipants: number,
) => {
  switch (calculateSidebarTypeFromSelectedQuestion(question)) {
    case SidebarType.Survey:
    case SidebarType.ImageSurvey:
      return buildSurveyTally(question, answers);
    case SidebarType.WidgetCategorizable:
      return buildCategorizableChart(question.Id, answers);
    case SidebarType.OpenAnswer:
      return { chart: { kind: 'openAnswer' }, responses: answers.length } as const;
    default:
      return buildHitRateChart(answers, totalParticipants);
  }
};

/**
 * Everything the host's analytics panel shows, derived from the session state that is
 * already synced to this client. No network, no server aggregation.
 */
export const buildSessionAnalytics = ({
  interactiveQuestionAnswers,
  interactiveQuestions,
  connectedUsernames = [],
}: {
  interactiveQuestionAnswers: InteractiveQuestionsState;
  interactiveQuestions: PlayPostMessageInteractiveQuestion[];
  /** Who is in the room right now: they count even before answering anything. */
  connectedUsernames?: string[];
}): SessionAnalytics => {
  const participantUsernames = buildParticipantUsernames({
    interactiveQuestionAnswers,
    connectedUsernames,
  });

  const totalParticipants = participantUsernames.length;

  // Only used to break ties in the participant list: whoever never answered has no
  // moment, so they fall to the end of their score group.
  const lastAnsweredAtByUsername = new Map<string, number>();

  Object.values(interactiveQuestionAnswers).forEach(state => {
    state.playerUserAnswers.forEach(({ player, answer }) => {
      const current = lastAnsweredAtByUsername.get(player.username);

      if (current === undefined || answer.timestamp > current) {
        lastAnsweredAtByUsername.set(player.username, answer.timestamp);
      }
    });
  });

  const resolved = interactiveQuestions
    .filter(question => !isBranchingScenario(question))
    .map(question => ({ question, state: interactiveQuestionAnswers[question.Id] }))
    .filter(
      (
        entry,
      ): entry is {
        question: PlayPostMessageInteractiveQuestion;
        state: InteractiveQuestionState;
      } => entry.state?.isResolved === true,
    )
    .map(({ question, state }) => {
      const answers = latestAnswerPerPlayer(state);
      const { chart, responses } = buildChart(question, answers, totalParticipants);

      return {
        question,
        isRandom: isRandomQuestion(question),
        responses,
        chart,
        respondents: answers.length,
        lastAnsweredAt: lastAnsweredAt(answers),
        // Only autocorrective answers carry `isCorrect`, so this filter is also what keeps
        // surveys and open answers out of the session hit rate.
        scoreable: answers.filter(isPlayerUserAnswerScoreable),
      };
    });

  // Most recently answered first, so what the host just resolved is on top without
  // scrolling. Resolved questions nobody answered have no moment, so they sink to the end.
  const ordered = [...resolved].sort((a, b) => {
    if (a.lastAnsweredAt === undefined) return b.lastAnsweredAt === undefined ? 0 : 1;
    if (b.lastAnsweredAt === undefined) return -1;

    return b.lastAnsweredAt - a.lastAnsweredAt;
  });

  const questions: SessionAnalyticsQuestion[] = ordered.map(
    ({ question, isRandom, responses, chart }) => ({
      question,
      isRandom,
      responses,
      chart,
    }),
  );

  if (resolved.length === 0) {
    return { kpis: undefined, questions, participants: [], scoreableQuestions: 0 };
  }

  const participationSum = resolved.reduce(
    (total, { respondents }) =>
      total + (totalParticipants === 0 ? 0 : respondents / totalParticipants),
    0,
  );

  // Weighted across every autocorrective answer rather than an average of the per-question
  // percentages, so this KPI is exactly what the bars below it add up to.
  const scoreableAnswers = resolved.flatMap(({ scoreable }) => scoreable);
  const correctAnswers = scoreableAnswers.filter(({ isCorrect }) => isCorrect).length;
  const wrongAnswers = scoreableAnswers.filter(({ isCorrect }) => !isCorrect).length;

  const correctByUsername = new Map<string, number>();

  scoreableAnswers.forEach(({ player, isCorrect }) => {
    if (isCorrect) {
      correctByUsername.set(
        player.username,
        (correctByUsername.get(player.username) ?? 0) + 1,
      );
    }
  });

  // Best first, so whoever needs following up is at the bottom edge rather than buried in
  // the middle. Ties fall back to who answered last and then to the name, which keeps the
  // order stable across re-renders instead of shuffling on every arriving answer.
  const answeredAt = (username: string) =>
    lastAnsweredAtByUsername.get(username) ?? Number.POSITIVE_INFINITY;

  const participants: SessionAnalyticsParticipant[] = [...participantUsernames]
    .sort(
      (usernameA, usernameB) =>
        (correctByUsername.get(usernameB) ?? 0) -
          (correctByUsername.get(usernameA) ?? 0) ||
        answeredAt(usernameA) - answeredAt(usernameB) ||
        usernameA.localeCompare(usernameB),
    )
    .map(username => ({
      username,
      correctAnswers: correctByUsername.get(username) ?? 0,
    }));

  // Questions that actually produced autocorrective answers, not everything that fell
  // through to the hit-rate chart: `buildChart` sends every unrecognised widget there
  // too, and counting those would inflate the denominator of every single row.
  const scoreableQuestions = resolved.filter(
    ({ scoreable }) => scoreable.length > 0,
  ).length;

  return {
    kpis: {
      resolvedQuestions: resolved.length,
      totalParticipants,
      averageParticipation: Math.round((participationSum / resolved.length) * 100),
      // Over every answer the room could have given — participants times autocorrective
      // questions — instead of over the answers that arrived, so hits, errors and silence
      // add up to the whole session rather than only to the part that spoke.
      hitRate: toPercentage(correctAnswers, totalParticipants * scoreableQuestions),
      errorRate: toPercentage(wrongAnswers, totalParticipants * scoreableQuestions),
      // One per (participant, question) pair, under the same deduplication rule as the
      // rest of the panel: a retry counts once, so this can't drift from the per-question
      // figures the way a raw count of received messages would.
      totalAnswers: resolved.reduce((total, { respondents }) => total + respondents, 0),
    },
    questions,
    participants,
    scoreableQuestions,
  };
};
